/* ══════════════════════════════════════════════
 *  React-Query hooks for the CaseFile page
 * ══════════════════════════════════════════════ */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import type {
  CaseFileResponse,
  CaseRecord,
  CaseNote,
  CaseTask,
  Solicitor,
  Defendant,
  DefendantSolicitor,
  StageHistoryEntry,
  CaseAuditEntry,
  CreateNotePayload,
  CreateTaskPayload,
  DraftTemplate,
  EmailTemplate,
} from '../types/caseFile';
import type { Stage } from '../types/cases';

/* ═══════════════════════════════════════════════
 *   Data fetching hooks
 * ═══════════════════════════════════════════════ */

/** Loads full case record + contact + related tables */
export function useCaseFile(caseRef: string | undefined) {
  return useQuery<CaseFileResponse>({
    queryKey: ['caseFile', caseRef],
    queryFn: async () => {
      const { data } = await api.get(`/api/casefile/${caseRef}`);
      // Merge single-row related tables into the case object (matches legacy behaviour)
      const caseData = { ...data.case } as CaseRecord;

      // IL Questionnaire — map q1–q15 → Q1–Q15
      if (
        Array.isArray(data.case_il_questionnaire_answers) &&
        data.case_il_questionnaire_answers.length > 0
      ) {
        const qa = data.case_il_questionnaire_answers[0] as Record<string, unknown>;
        for (let i = 1; i <= 15; i++) {
          const lower = `q${i}`;
          const upper = `Q${i}`;
          if (qa[lower] !== undefined) caseData[upper] = qa[lower];
        }
      }

      // DSAR Assessment — map snake_case → CamelCase
      if (
        Array.isArray(data.case_dsar_assessment_outcomes) &&
        data.case_dsar_assessment_outcomes.length > 0
      ) {
        const dsar = data.case_dsar_assessment_outcomes[0] as Record<string, unknown>;
        const dsarMap: Record<string, string> = {
          tpo_date: 'TPODate',
          start_date: 'StartDate',
          account_number: 'AccountNumber',
          estimate_quantum: 'EstimateQuantum',
          claim_form_value: 'ClaimFormValue',
          court_fee: 'CourtFee',
          amount_to_client: 'AmountToClient',
          total_costs: 'TotalCosts',
          success_fee: 'SuccessFee',
        };
        for (const [from, to] of Object.entries(dsarMap)) {
          if (dsar[from] !== undefined) caseData[to] = dsar[from];
        }
      }

      // Settlements
      if (Array.isArray(data.case_settlements) && data.case_settlements.length > 0) {
        const sett = data.case_settlements[0] as Record<string, unknown>;
        const settMap: Record<string, string> = {
          settlement_date: 'SettlementDate',
          general_damages: 'GeneralDamages',
          success_fee: 'SuccessFee',
          amount_to_client: 'AmountToClient',
          total_costs: 'TotalCosts',
        };
        for (const [from, to] of Object.entries(settMap)) {
          if (sett[from] !== undefined) caseData[to] = sett[from];
        }
      }

      // Litigation
      if (Array.isArray(data.case_litigation) && data.case_litigation.length > 0) {
        const lit = data.case_litigation[0] as Record<string, unknown>;
        Object.assign(caseData, lit);
      }

      return { ...data, case: caseData };
    },
    enabled: !!caseRef,
    staleTime: 60_000,
  });
}

/** Load case notes */
export function useCaseNotes(caseRef: string | undefined) {
  return useQuery<CaseNote[]>({
    queryKey: ['caseNotes', caseRef],
    queryFn: async () => {
      const { data } = await api.get(`/api/casefile/${caseRef}/notes`);
      return data.notes ?? [];
    },
    enabled: !!caseRef,
  });
}

/** Load case tasks */
export function useCaseTasks(caseRef: string | undefined) {
  return useQuery<CaseTask[]>({
    queryKey: ['caseTasks', caseRef],
    queryFn: async () => {
      const { data } = await api.get(`/api/casefile/${caseRef}/tasks`);
      return data.tasks ?? [];
    },
    enabled: !!caseRef,
  });
}

/** Load solicitors for dropdown (from DB users table) */
export function useSolicitors() {
  return useQuery<Solicitor[]>({
    queryKey: ['solicitors'],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/dynamic/solicitors');
      const list = data.data ?? data.users ?? data.solicitors ?? [];
      if (!Array.isArray(list)) return [];
      return list.map((row: Record<string, string>) => ({
        cognito_user_id: row.cognito_user_id || row.value || '',
        username: row.username || row.label || '',
        displayName: row.label || row.username || '',
      }));
    },
    staleTime: 5 * 60_000,
  });
}

/** Load defendants (paginated, fetch all) */
export function useDefendants() {
  return useQuery<Defendant[]>({
    queryKey: ['defendants'],
    queryFn: async () => {
      const all: Defendant[] = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const { data } = await api.get(`/api/defendants?page=${page}&limit=200`);
        const items = data.defendants ?? data.data ?? [];
        all.push(...items);
        hasMore = items.length === 200;
        page++;
      }
      return all;
    },
    staleTime: 5 * 60_000,
  });
}

/** Load defendant solicitors (paginated, fetch all) */
export function useDefendantSolicitors() {
  return useQuery<DefendantSolicitor[]>({
    queryKey: ['defendantSolicitors'],
    queryFn: async () => {
      const all: DefendantSolicitor[] = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const { data } = await api.get(`/api/defendant-solicitors?page=${page}&limit=200`);
        const items = data.defendant_solicitors ?? data.data ?? [];
        all.push(...items);
        hasMore = items.length === 200;
        page++;
      }
      return all;
    },
    staleTime: 5 * 60_000,
  });
}

/** Load stages for a case type by ID */
export function useCaseStages(caseTypeId: number | null) {
  return useQuery<Stage[]>({
    queryKey: ['caseStages', caseTypeId],
    queryFn: async () => {
      const { data } = await api.get(`/api/stages/case-type/${caseTypeId}`);
      return data.stages ?? data.data ?? [];
    },
    enabled: caseTypeId != null,
    staleTime: 5 * 60_000,
  });
}

/** Load stage history */
export function useStageHistory(caseRef: string | undefined, enabled: boolean) {
  return useQuery<StageHistoryEntry[]>({
    queryKey: ['stageHistory', caseRef],
    queryFn: async () => {
      const { data } = await api.get(`/api/casefile/${caseRef}/stage-history`);
      return data.data ?? [];
    },
    enabled: !!caseRef && enabled,
  });
}

/** Load merged audit timeline for a case (lifecycle + field changes) */
export function useCaseAuditLog(
  caseRef: string | undefined,
  enabled: boolean,
  options?: { limit?: number; offset?: number },
) {
  return useQuery<{
    caseRef: string;
    rows: CaseAuditEntry[];
    count: number;
    limit: number;
    offset: number;
  }>({
    queryKey: ['caseAuditLog', caseRef, options?.limit, options?.offset],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', String(options.limit));
      if (options?.offset) params.append('offset', String(options.offset));

      const { data } = await api.get(
        `/api/admin/audit/case/${caseRef}${params.toString() ? `?${params.toString()}` : ''}`,
      );
      return data;
    },
    enabled: !!caseRef && enabled,
  });
}

/* ═══════════════════════════════════════════════
 *   Mutations
 * ═══════════════════════════════════════════════ */

/** Save / update case fields */
export function useUpdateCase(caseRef: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: Record<string, unknown>) => {
      const { data } = await api.put(`/api/casefile/${caseRef}`, fields);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caseFile', caseRef] });
    },
  });
}

/** Add a note */
export function useAddNote(caseRef: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateNotePayload) => {
      const { data } = await api.post(`/api/casefile/${caseRef}/notes`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caseNotes', caseRef] });
    },
  });
}

/** Create a task */
export function useCreateTask(caseRef: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateTaskPayload) => {
      const { data } = await api.post(`/api/casefile/${caseRef}/tasks`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caseTasks', caseRef] });
      qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['allTasks'] });
      qc.invalidateQueries({ queryKey: ['taskCalendar'] });
    },
  });
}

/** Update a task (uses /api/home/task/update) */
export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { TaskID: string; [key: string]: unknown }) => {
      const { data } = await api.post('/api/home/task/update', payload);
      return data;
    },
    onSuccess: () => {
      // Invalidate all task queries (case file + home + tasks page + calendar)
      qc.invalidateQueries({ queryKey: ['caseTasks'] });
      qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['allTasks'] });
      qc.invalidateQueries({ queryKey: ['taskCalendar'] });
    },
  });
}

/** Delete a task */
export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      const { data } = await api.post('/api/home/task/delete', { TaskID: taskId });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caseTasks'] });
      qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['allTasks'] });
      qc.invalidateQueries({ queryKey: ['taskCalendar'] });
    },
  });
}

/** Add a time entry (matches legacy payload: Duration, StartTime, EndTime, Task, Units, User) */
export function useAddTimeEntry(caseRef: string | undefined) {
  return useMutation({
    mutationFn: async (payload: {
      Duration: string;
      StartTime: string;
      EndTime: string;
      Task: string;
      Units: number;
      User: string;
    }) => {
      const { data } = await api.post(`/api/casefile/${caseRef}/timeentry`, payload);
      return data;
    },
  });
}

/** Reassign case to a different contact */
export function useReassignCase(caseRef: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (newContactId: string) => {
      const { data } = await api.put(`/api/casefile/${caseRef}/reassign`, { newContactId });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caseFile', caseRef] });
    },
  });
}

/** Fetch letter templates for Draft Assistant */
export function useDraftTemplates(caseTypeId: number | null | undefined) {
  return useQuery<DraftTemplate[]>({
    queryKey: ['draftTemplates', caseTypeId],
    queryFn: async () => {
      const params = caseTypeId ? `?caseTypeId=${caseTypeId}` : '';
      const { data } = await api.get(`/api/documents/draft-templates${params}`);
      return data.templates ?? data.data ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

/** Fetch email templates for Draft Assistant */
export function useEmailTemplates() {
  return useQuery<EmailTemplate[]>({
    queryKey: ['emailTemplates'],
    queryFn: async () => {
      const { data } = await api.get(`/api/documents/da-email-templates`);
      return data.templates ?? data.data ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

/** Save risk assessment */
export function useSaveRiskAssessment() {
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await api.post('/api/casefile/risk-assessment', payload);
      return data;
    },
  });
}

export function useSavePreIssueChecklist() {
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await api.post('/api/casefile/pre-issue-checklist', payload);
      return data;
    },
  });
}

/** Search contacts (for reassign) */
export function useSearchContacts() {
  return useMutation({
    mutationFn: async (query: string) => {
      const { data } = await api.get(`/api/contacts/search?q=${encodeURIComponent(query)}`);
      return Array.isArray(data) ? data : (data.results ?? data.contacts ?? []);
    },
  });
}

/** Delete a case (superadmin only) */
export function useDeleteCase(caseRef: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(`/api/casefile/${caseRef}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caseFile', caseRef] });
      qc.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

/* ── Portal Notes (CRM staff) ── */

export interface PortalNote {
  id: number;
  case_ref: string;
  author_id: string;
  author_name: string;
  body: string;
  created_at: string;
  updated_at: string;
}

export function usePortalNotes(caseRef: string | undefined) {
  return useQuery<{ notes: PortalNote[] }>({
    queryKey: ['portalNotes', caseRef],
    queryFn: async () => {
      const { data } = await api.get(`/api/portal-manage/notes/${caseRef}`);
      return data;
    },
    enabled: !!caseRef,
  });
}

export function useCreatePortalNote(caseRef: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const { data } = await api.post(`/api/portal-manage/notes/${caseRef}`, { body });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portalNotes', caseRef] }),
  });
}

export function useDeletePortalNote(caseRef: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: number) => {
      const { data } = await api.delete(`/api/portal-manage/notes/${noteId}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portalNotes', caseRef] }),
  });
}
